import React, { useEffect, useState } from "react";
import "../../App.css";
import service from "../../services/service";
import endpoints from "../../services/endpoints";
import { Eye, EyeOff, Camera } from "lucide-react";
import { useParams } from "react-router-dom";

const Profile = () => {
  const params = useParams();
  const [activeTab, setActiveTab] = useState("profile");
  const [connectedApps, setConnectedApps] = useState([]);
  const [activityHistory, setActivityHistory] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [distancePreference, setDistancePreference] = useState("KM");

  const [activityPage, setActivityPage] = useState(1);
  const [activityTotalPages, setActivityTotalPages] = useState(1);

  const [profileData, setProfileData] = useState({
    fullName: "",
    phone: "",
    countryCode: "+1",
    email: "",
    profileImage: "",
  });

  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [loading, setLoading] = useState(false);

  const [passwordModal, setPasswordModal] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const countryCodes = [
    { code: "+1", label: "US (+1)" },
    { code: "+1", label: "CA (+1)" },
    { code: "+44", label: "UK (+44)" },
    { code: "+91", label: "IN (+91)" },
    { code: "+61", label: "AU (+61)" },
    { code: "+64", label: "NZ (+64)" },
    { code: "+86", label: "CN (+86)" },
    { code: "+81", label: "JP (+81)" },
    { code: "+82", label: "KR (+82)" },
    { code: "+65", label: "SG (+65)" },
    { code: "+49", label: "DE (+49)" },
    { code: "+33", label: "FR (+33)" },
    { code: "+39", label: "IT (+39)" },
    { code: "+34", label: "ES (+34)" },
    { code: "+31", label: "NL (+31)" },
    { code: "+41", label: "CH (+41)" },
    { code: "+971", label: "AE (+971)" },
    { code: "+966", label: "SA (+966)" },
    { code: "+27", label: "ZA (+27)" },
    { code: "+55", label: "BR (+55)" },
    { code: "+52", label: "MX (+52)" },
  ];

  /* =========================
      GET PROFILE
  ========================= */

  const getProfile = async () => {
    try {
      const response = await service.get(endpoints.user.getProfile);

      const user = response?.data?.data;

      setProfileData({
        fullName: user?.fullName || "",
        phone: user?.phone || "",
        countryCode: user?.countryCode || "+1",
        email: user?.email || "",
        profileImage: user?.profileImage || "",
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getProfile();
    getConnectedApps();
    getDistancePreference();
  }, []);

  useEffect(() => {
    getActivityHistory(activityPage);
  }, [activityPage]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const stravaStatus = params.get("strava");

    if (stravaStatus === "success") {
      getConnectedApps();

      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  /* =========================
      UPDATE PROFILE
  ========================= */

  const handleUpdateProfile = async () => {
    try {
      setLoading(true);

      await service.patch(
        endpoints.user.updateProfile,
        {
          fullName: profileData.fullName,
          phone: profileData.phone,
          countryCode: profileData.countryCode,
        },
        {
          showSuccessToast: true,
        },
      );

      setIsEditingName(false);
      setIsEditingPhone(false);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  /* =========================
      UPDATE PROFILE IMAGE
  ========================= */

  const handleProfileImage = async (e) => {
    try {
      const file = e.target.files[0];

      if (!file) return;

      const formData = new FormData();

      formData.append("profileImage", file);

      await service.patch(endpoints.user.updateProfileImage, formData, {
        showSuccessToast: true,
      });

      getProfile();
    } catch (error) {
      console.log(error);
    }
  };

  /* =========================
      CHANGE PASSWORD
  ========================= */

  const handleChangePassword = async () => {
    try {
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        return alert("Passwords do not match");
      }

      await service.patch(endpoints.user.changePassword, passwordData, {
        showSuccessToast: true,
      });

      setPasswordModal(false);

      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.log(error);
    }
  };

  const getConnectedApps = async () => {
    try {
      const response = await service.get(endpoints.user.connectedApps);

      setConnectedApps(response?.data?.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  const handleConnectStrava = async () => {
    try {
      const response = await service.get(endpoints.user.stravaAuthUrl);

      const authUrl = response?.data?.data?.authUrl;

      if (authUrl) {
        window.location.href = authUrl;
      }
    } catch (error) {
      console.log(error);
    }
  };

  /* =========================
      DISCONNECT STRAVA
  ========================= */

  const handleDisconnectStrava = async () => {
    try {
      await service.post(
        endpoints.user.disconnectStrava,
        {}, 
        {
          showSuccessToast: true,
        }
      );

      getConnectedApps();
    } catch (error) {
      console.log(error);
    }
  };

  const stravaApp = connectedApps.find((app) => app.providerName === "STRAVA");

  const getActivityHistory = async (page = 1) => {
    try {
      setActivityLoading(true);

      const response = await service.get(
        `${endpoints.user.activityHistory}?page=${page}&limit=10`,
      );

      const responseData = response?.data?.data;

      setActivityHistory(responseData?.items || []);

      setActivityPage(responseData?.pagination?.page || 1);

      setActivityTotalPages(responseData?.pagination?.totalPages || 1);
    } catch (error) {
      console.log(error);
    } finally {
      setActivityLoading(false);
    }
  };

  /* =========================
    UPDATE DISTANCE PREFERENCE
  ========================= */

  const handleDistancePreference = async (value) => {
    try {
      setDistancePreference(value);

      await service.patch(
        endpoints.user.distancePreference,
        {
          distancePreferenceUnit: value,
        },
        {
          showSuccessToast: true,
        },
      );
    } catch (error) {
      console.log(error);
    }
  };

  const getDistancePreference = async () => {
    try {
      const response = await service.get(endpoints.user.distancePreference);
      const unit = response?.data?.data?.distancePreferenceUnit;
      if (unit) setDistancePreference(unit);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className="profile-page">
      {/* TITLE */}
      <h1 className="profile-page-title">My Profile</h1>

      {/* TABS */}
      <div className="profile-tabs">
        <button
          className={`profile-tab ${activeTab === "profile" ? "active" : ""}`}
          onClick={() => setActiveTab("profile")}
        >
          Profile Details
        </button>

        <button
          className={`profile-tab ${activeTab === "apps" ? "active" : ""}`}
          onClick={() => setActiveTab("apps")}
        >
          Connected Apps
        </button>

        <button
          className={`profile-tab ${activeTab === "history" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("history");

            setActivityPage(1);

            getActivityHistory(1);
          }}
        >
          Activity History
        </button>

        <button
          className={`profile-tab ${
            activeTab === "preferences" ? "active" : ""
          }`}
          onClick={() => setActiveTab("preferences")}
        >
          Preferences
        </button>
      </div>

      {/* PROFILE DETAILS */}
      {activeTab === "profile" && (
        <div className="profile-details-section">
          <div className="profile-header">
            <div className="profile-avatar">
              {profileData.profileImage ? (
                <img src={profileData.profileImage} alt="profile" />
              ) : (
                <span>👤</span>
              )}

              {/* HIDDEN FILE INPUT */}
              <input
                type="file"
                id="profileImageUpload"
                hidden
                accept="image/*"
                onChange={handleProfileImage}
              />

              {/* CAMERA BUTTON */}
              <label htmlFor="profileImageUpload" className="camera-icon">
                <Camera size={16} />
              </label>
            </div>

            <div className="profile-edit">
              <p>Profile Picture</p>

              {/* EDIT BUTTON */}
              <label htmlFor="profileImageUpload" className="edit-profile-btn">
                Edit Profile Image
              </label>
            </div>
          </div>

          <div className="profile-form">
            <div className="form-group">
              <label>Full Name</label>
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <input
                  type="text"
                  value={profileData.fullName}
                  readOnly={!isEditingName}
                  onChange={(e) =>
                    setProfileData({
                      ...profileData,
                      fullName: e.target.value,
                    })
                  }
                  style={{ width: "100%", paddingRight: "60px" }}
                />
                {!isEditingName && (
                  <button
                    type="button"
                    onClick={() => setIsEditingName(true)}
                    style={{
                      position: "absolute",
                      right: "20px",
                      background: "none",
                      border: "none",
                      color: "#FF0000",
                      fontWeight: "bold",
                      cursor: "pointer",
                    }}
                  >
                    EDIT
                  </button>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    width: "100%",
                    paddingRight: !isEditingPhone ? "60px" : "0px",
                  }}
                >
                  {isEditingPhone ? (
                    <select
                      value={profileData.countryCode}
                      onChange={(e) =>
                        setProfileData({
                          ...profileData,
                          countryCode: e.target.value,
                        })
                      }
                      style={{
                        flex: 2,
                        padding: "0 12px",
                        borderRadius: "8px",
                        height: "45px",
                      }}
                    >
                      {countryCodes.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={profileData.countryCode}
                      readOnly
                      style={{
                        width: "60px",
                        textAlign: "center",
                        pointerEvents: "none",
                      }}
                    />
                  )}

                  <input
                    type="text"
                    value={profileData.phone}
                    readOnly={!isEditingPhone}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        phone: e.target.value,
                      })
                    }
                    style={{ flex: 1 }}
                  />
                </div>
                {!isEditingPhone && (
                  <button
                    type="button"
                    onClick={() => setIsEditingPhone(true)}
                    style={{
                      position: "absolute",
                      right: "20px",
                      background: "none",
                      border: "none",
                      color: "#FF0000",
                      fontWeight: "bold",
                      cursor: "pointer",
                    }}
                  >
                    EDIT
                  </button>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Email ID</label>

              <input type="email" value={profileData.email} readOnly />
            </div>

            <div className="profile-btns">
              <button
                className="save-btn"
                onClick={handleUpdateProfile}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>

              <button
                className="password-btn"
                onClick={() => setPasswordModal(true)}
              >
                Change Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONNECTED APPS */}
      {activeTab === "apps" && (
        <div className="connected-app-card">
          <div className="app-left">
            <div className="app-icon">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/c/cb/Strava_Logo.svg"
                alt="strava"
              />
            </div>

            <div className="app-details">
              <h3>Strava</h3>

              <p>{stravaApp?.isConnected ? "Connected" : "Not Connected"}</p>
            </div>
          </div>

          <div className="app-actions">
            {stravaApp?.isConnected ? (
              <>
                <button className="sync-btn">↻</button>

                <button
                  className="disconnect-btn"
                  onClick={handleDisconnectStrava}
                >
                  Disconnect
                </button>
              </>
            ) : (
              <button className="save-btn" onClick={handleConnectStrava}>
                Connect Strava
              </button>
            )}
          </div>
        </div>
      )}

      {/* ACTIVITY HISTORY */}
      {activeTab === "history" && (
        <div className="activity-table">
          <div className="activity-header">
            <span>ACTIVITY</span>
            <span>TIME</span>
          </div>

          {activityLoading ? (
            <p className="loading-text">Loading activity history...</p>
          ) : activityHistory?.length > 0 ? (
            <>
              {activityHistory.map((item) => (
                <div className="activity-row" key={item?.id}>
                  <span>{item?.activity}</span>

                  <span>
                    {item?.time || new Date(item?.createdAt).toLocaleString()}
                  </span>
                </div>
              ))}

              {/* PAGINATION */}
              <div className="activity-pagination">
                <button
                  disabled={activityPage === 1}
                  onClick={() => setActivityPage((prev) => prev - 1)}
                >
                  Previous
                </button>

                <span>
                  Page {activityPage} of {activityTotalPages}
                </span>

                <button
                  disabled={activityPage === activityTotalPages}
                  onClick={() => setActivityPage((prev) => prev + 1)}
                >
                  Next
                </button>
              </div>
            </>
          ) : (
            <p className="loading-text">No activity history found</p>
          )}
        </div>
      )}

      {/* PREFERENCES */}
      {activeTab === "preferences" && (
        <div className="preferences-section">
          <label>Distance Preference</label>

          <select
            value={distancePreference}
            onChange={(e) => handleDistancePreference(e.target.value)}
          >
            <option value="KM">Kilometers (KM)</option>
            <option value="MILES">Miles</option>
          </select>
        </div>
      )}

      {/* PASSWORD MODAL */}
      {passwordModal && (
        <div className="password-modal-overlay">
          <div className="password-modal">
            <h2>Reset Password</h2>

            <p>Enter your New & Confirm Password</p>

            <div className="password-group">
              <label>Current Password</label>

              <div className="password-input">
                <input
                  type={showPassword ? "text" : "password"}
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      currentPassword: e.target.value,
                    })
                  }
                />

                <button onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="password-group">
              <label>New Password</label>

              <div className="password-input">
                <input
                  type={showPassword ? "text" : "password"}
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      newPassword: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className="password-group">
              <label>Confirm Password</label>

              <div className="password-input">
                <input
                  type={showPassword ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      confirmPassword: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className="password-modal-btns">
              <button
                className="save-password-btn"
                onClick={handleChangePassword}
              >
                Save
              </button>

              <button
                className="cancel-password-btn"
                onClick={() => setPasswordModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;